import { PropsWithChildren, ReactNode } from "react";

import { DateTime } from "@bps/date-time";
import {
  dataAttribute,
  DataAttributes,
  IconButton,
  MessageBar,
  MessageBarType,
  Stack
} from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { ClinicalRecord } from "@stores/clinical/models/ClinicalRecord.ts";
import { When } from "@ui-components/withPerm.tsx";

import { FormHeaderWrapper } from "../../clinical-form/FormHeaderWrapper.tsx";

export type ClinicalToolViewProps = PropsWithChildren<{
  clinicalRecord: ClinicalRecord;
  title: string;
  summary?: string;
  subtitle?: string;
  encounterId?: string;
  isConfidential: boolean;
  lastUpdatedDate?: DateTime;
  lastUpdatedUserId?: string;
  onRenderSummary?: () => ReactNode;
}>;

export const ClinicalToolView: React.FunctionComponent<
  ClinicalToolViewProps
> = ({
  clinicalRecord,
  title,
  summary,
  children,
  isConfidential,
  lastUpdatedDate,
  lastUpdatedUserId,
  onRenderSummary
}) => {
  const confidentialShield = () => {
    return (
      <When permission={Permission.ConfidentialDataAllowed}>
        <IconButton
          iconProps={{ iconName: isConfidential ? "ShieldAlert" : "Shield" }}
          disabled
        />
      </When>
    );
  };

  return (
    <FormHeaderWrapper
      heading={title}
      confidentialShield={confidentialShield()}
      lastUpdatedDate={lastUpdatedDate}
      lastUpdatedUserId={lastUpdatedUserId}
      onClose={() => clinicalRecord.patientRecordTab?.hideActive()}
      noGap
      hasSeparator
    >
      <Stack
        tokens={{ childrenGap: 16 }}
        styles={{
          root: { overflowY: "auto" }
        }}
      >
        {onRenderSummary
          ? onRenderSummary()
          : summary && (
              <MessageBar
                {...dataAttribute(
                  DataAttributes.Element,
                  "observation-summary"
                )}
                messageBarType={MessageBarType.info}
              >
                {summary}
              </MessageBar>
            )}

        {children}
      </Stack>
    </FormHeaderWrapper>
  );
};
